import {type AtpAgent} from '@atproto/api'
import {useMutation, useQueryClient} from '@tanstack/react-query'

import {BSKY_SERVICE} from '#/lib/constants'
import {ASSETS_RQKEY} from '#/state/queries/bills'
import {useAgent} from '#/state/session'
import {type FlashBookPageResult} from '#/types/flashbook'

/** A finished read, in the shape the endpoint takes. */
export type FlashbookPlay = {
  flashbookUri: string
  score: number
  timeMs: number
  pageResults: FlashBookPageResult[]
}

/** Post one finished read, and answer with the play the AppView made of it. */
export async function createFlashbookPlay(
  agent: AtpAgent,
  {flashbookUri, score, timeMs, pageResults}: FlashbookPlay,
): Promise<{playId: string}> {
  const accessJwt = agent.session?.accessJwt
  if (!accessJwt) throw new Error('Not authenticated')

  const res = await fetch(
    `${BSKY_SERVICE}/xrpc/app.bsky.feed.createFlashbookPlay`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessJwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({flashbookUri, score, timeMs, pageResults}),
    },
  )

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message ?? `HTTP ${res.status}`)
  }

  return res.json() as Promise<{playId: string}>
}

export function useCreateFlashbookPlayMutation() {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      flashbookUri,
      score,
      timeMs,
      pageResults,
    }: {
      flashbookUri: string
      score: number
      timeMs: number
      pageResults: FlashBookPageResult[]
    }) => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const res = await fetch(
        `${BSKY_SERVICE}/xrpc/app.bsky.feed.createFlashbookPlay`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessJwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({flashbookUri, score, timeMs, pageResults}),
        },
      )

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }

      return res.json() as Promise<{playId: string}>
    },
    onSuccess: () => {
      const did = agent.session?.did
      if (!did) return
      void queryClient.invalidateQueries({queryKey: ASSETS_RQKEY(did)})
    },
  })
}
